import {
  ArrowLeft,
  Camera,
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  Expand,
  FileText,
  LoaderCircle,
  MonitorPlay,
  Presentation,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react'
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { HelpTooltip } from '../components/HelpTooltip'
import { buildCanvasProjection } from '../lib/canvasProjection'
import {
  buildPresentationSlides,
  buildPresentationSourceSnapshot,
  PRESENTATION_DEPTH_LABELS,
  PRESENTATION_STATIC_COPY,
  presentationConfigFor,
} from '../lib/presentation'
import { formatDate } from '../lib/project'
import { captureMatchesPresentation } from '../modules/canvas3d/captureBinding'
import type { PresentationCaptureDraft } from '../modules/canvas3d/types'
import { buildPresentationPowerPoint, sha256Blob } from '../services/presentationFiles'
import type {
  PresentationDraftConfig,
  PresentationDuration,
  PresentationEditionRow,
  PresentationSlide,
  PresentationSourceSnapshot,
  ProjectAttachment,
  ProjectRow,
  PresentationScope,
} from '../types'

const CaptureWorkbench = lazy(() => import('../modules/canvas3d/CaptureWorkbench').then((module) => ({ default: module.CaptureWorkbench })))

type PublishInput = {
  id: string
  editionId: string
  config: PresentationDraftConfig
  source: PresentationSourceSnapshot
  pptx: Blob
  pptxSha256: string
}

type PresentationStudioProps = {
  project: ProjectRow
  scope: PresentationScope
  attachments: ProjectAttachment[]
  editions: PresentationEditionRow[]
  onBack: () => void
  onPublish: (input: PublishInput) => Promise<PresentationEditionRow>
  onDownload: (edition: PresentationEditionRow) => Promise<void>
}

function editionIdFor(project: ProjectRow, scope: PresentationScope) {
  const stamp = new Date().toISOString().replace(/[-:.TZ]/g, '').slice(0, 17)
  return scope === 'general' ? `PRES-REF-${stamp}` : `PRES-${project.code}-${stamp}`
}

function PresentationSlideCanvas({ slide, capture, large = false }: { slide: PresentationSlide; capture?: PresentationCaptureDraft; large?: boolean }) {
  const toneClass = slide.key === 'edition' ? 'edition' : slide.key === 'cover' ? 'cover' : ''
  return (
    <article className={`presentation-slide ${toneClass} ${large ? 'large' : ''}`} aria-label={`Diapositive ${slide.title}`}>
      {slide.key === 'cover' ? (
        <>
          <div className="presentation-cover-band">
            <img src="/assets/brand/sablux-ci-logo.png" alt="Sablux Côte d’Ivoire" />
            <h2>{slide.title}</h2>
            <p>{slide.subtitle}</p>
            <span>{slide.tokens.AS_OF}</span>
            <small>{slide.tokens.SCOPE_LABEL}</small>
          </div>
          <div className="presentation-cover-message">
            <img src="/assets/brand/sablux-ci-logo.png" alt="" aria-hidden="true" />
            <h3>{slide.tokens.COVER_HEADING ?? PRESENTATION_STATIC_COPY.coverHeading}</h3>
            <p>{slide.statement}</p>
            <strong>{slide.detail}</strong>
            <small>{slide.tokens.EDITION_ID}</small>
          </div>
        </>
      ) : slide.key === 'edition' ? (
        <div className="presentation-edition-slide">
          <span>{slide.tokens.EDITION_KICKER ?? PRESENTATION_STATIC_COPY.editionKicker}</span>
          <h2>{slide.title}</h2>
          <div>
            <section><small>IDENTIFIANT</small><strong>{slide.tokens.EDITION_ID}</strong><small>SITUATION</small><strong>{slide.tokens.AS_OF}</strong></section>
            <section><small>SOURCE</small><strong>{slide.detail}</strong><small>CONTENU</small><p>{slide.tokens.CONTENT_SUMMARY}</p></section>
          </div>
        </div>
      ) : (
        <>
          <header className="presentation-slide-header">
            <img src="/assets/brand/sablux-ci-logo.png" alt="Sablux Côte d’Ivoire" />
            <span>SUIVI AZIZ</span>
          </header>
          <div className="presentation-slide-heading">
            <h2>{slide.title}</h2>
            {slide.subtitle ? <p>{slide.subtitle}</p> : null}
          </div>
          <div className={`presentation-slide-body layout-${slide.key}`}>
            {capture ? (
              <figure className="presentation-slide-capture" data-capture-id={capture.binding.id}>
                <img alt={`Vue 3D · ${slide.title}`} src={capture.previewUrl} />
              </figure>
            ) : (
              <>
                {slide.phases ? <div className="presentation-process-line">{slide.phases.map((phase, index) => <div key={`${phase.name}-${index}`}><span>{index + 1}</span><strong>{phase.name}</strong><small>{phase.detail}</small></div>)}</div> : null}
                {slide.key === 'current' ? <div className="presentation-current"><section><small>{slide.kicker}</small><h3>{slide.statement}</h3><strong>{slide.tokens.CURRENT_STATUS}</strong><p>{slide.tokens.CURRENT_DETAIL}</p></section><aside><b>{slide.metric}</b><strong>{slide.metricLabel}</strong><p>{slide.detail}</p></aside></div> : null}
                {slide.columns ? <div className="presentation-columns">{slide.columns.map((column) => <section className={column.tone} key={column.label}><small>{column.label}</small>{column.items.map((item) => <p key={item}>{item}</p>)}</section>)}</div> : null}
                {slide.timeline ? <div className="presentation-timeline">{slide.timeline.map((item, index) => <div key={`${item.date}-${item.label}`}><strong>{item.date}</strong><span>{index + 1}</span><p>{item.label}</p></div>)}<footer>{slide.detail}</footer></div> : null}
                {slide.key === 'derogation' ? <div className="presentation-derogation"><section><h3>{slide.statement}</h3><p>{slide.detail}</p></section><aside>{slide.items?.map((item, index) => <div key={item.title}><span>D{(slide.sequenceStart ?? 0) + index + 1}</span><strong>{item.title}</strong></div>)}</aside></div> : null}
                {slide.items && slide.key !== 'derogation' ? <div className="presentation-items">{slide.items.map((item, index) => <div key={`${item.title}-${index}`}><span>{String((slide.sequenceStart ?? 0) + index + 1).padStart(2, '0')}</span><strong>{item.title}</strong><small>{[item.detail, item.meta].filter(Boolean).join(' · ')}</small></div>)}</div> : null}
              </>
            )}
          </div>
          <footer className="presentation-slide-footer">Sablux Côte d’Ivoire · {slide.tokens.EDITION_ID}</footer>
        </>
      )}
    </article>
  )
}

export function PresentationStudio({ project, scope, attachments, editions, onBack, onPublish, onDownload }: PresentationStudioProps) {
  const [config, setConfig] = useState(() => presentationConfigFor(scope))
  const [slideIndex, setSlideIndex] = useState(0)
  const [presenting, setPresenting] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null)
  const [latestEditionId, setLatestEditionId] = useState<string | null>(null)
  const [draftId, setDraftId] = useState(() => crypto.randomUUID())
  const [capturesBySlide, setCapturesBySlide] = useState<Map<string, PresentationCaptureDraft>>(() => new Map())
  const [captureSlideId, setCaptureSlideId] = useState<string | null>(null)
  const presentationRef = useRef<HTMLDivElement>(null)
  const capturePreviewUrls = useRef(new Set<string>())
  const previewEditionId = scope === 'general' ? 'APERÇU-RÉFÉRENCE' : `APERÇU-${project.code}`
  const projection = useMemo(() => buildCanvasProjection(project, scope), [project, scope])
  const referenceProjection = useMemo(() => buildCanvasProjection(project, 'general'), [project])
  const slides = useMemo(
    () => buildPresentationSlides(project, attachments, config, previewEditionId),
    [attachments, config, project],
  )
  const currentSlide = slides[slideIndex]
  const captureCandidate = currentSlide ? capturesBySlide.get(currentSlide.id) : undefined
  const currentCapture = captureCandidate && currentSlide && captureMatchesPresentation(captureCandidate.binding, {
    draftId,
    slideId: currentSlide.id,
    scope,
    projection,
    referenceProjection,
  }) ? captureCandidate : undefined
  const captureSlide = captureSlideId ? slides.find((slide) => slide.id === captureSlideId) : undefined

  useEffect(() => setSlideIndex((current) => Math.min(current, Math.max(0, slides.length - 1))), [slides.length])

  useEffect(() => {
    setConfig(presentationConfigFor(scope))
    setSlideIndex(0)
  }, [project.id, project.version, scope])

  useEffect(() => {
    for (const previewUrl of capturePreviewUrls.current) URL.revokeObjectURL?.(previewUrl)
    capturePreviewUrls.current.clear()
    setCapturesBySlide(new Map())
    setCaptureSlideId(null)
    setDraftId(crypto.randomUUID())
  }, [config.duration, project.id, project.version, scope])

  useEffect(() => () => {
    for (const previewUrl of capturePreviewUrls.current) URL.revokeObjectURL?.(previewUrl)
    capturePreviewUrls.current.clear()
  }, [])

  useEffect(() => {
    if (!presenting) return
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' || event.key === ' ') setSlideIndex((current) => Math.min(slides.length - 1, current + 1))
      if (event.key === 'ArrowLeft') setSlideIndex((current) => Math.max(0, current - 1))
      if (event.key === 'Escape') setPresenting(false)
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [presenting, slides.length])

  function toggleAttachment(id: string) {
    setConfig((current) => ({
      ...current,
      selectedAttachmentIds: current.selectedAttachmentIds.includes(id)
        ? current.selectedAttachmentIds.filter((attachmentId) => attachmentId !== id)
        : [...current.selectedAttachmentIds, id],
    }))
  }

  function keepCapture(capture: PresentationCaptureDraft) {
    if (!captureSlideId || !captureMatchesPresentation(capture.binding, {
      draftId,
      slideId: captureSlideId,
      scope,
      projection,
      referenceProjection,
    })) {
      URL.revokeObjectURL?.(capture.previewUrl)
      setMessage({ tone: 'error', text: 'Cette vue 3D ne correspond plus à cette présentation.' })
      return
    }
    const previous = capturesBySlide.get(captureSlideId)
    if (previous && previous.previewUrl !== capture.previewUrl) {
      URL.revokeObjectURL?.(previous.previewUrl)
      capturePreviewUrls.current.delete(previous.previewUrl)
    }
    capturePreviewUrls.current.add(capture.previewUrl)
    const next = new Map(capturesBySlide)
    next.set(captureSlideId, capture)
    setCapturesBySlide(next)
    setCaptureSlideId(null)
    setMessage(null)
  }

  function removeCapture(slideId: string) {
    const capture = capturesBySlide.get(slideId)
    if (!capture) return
    URL.revokeObjectURL?.(capture.previewUrl)
    capturePreviewUrls.current.delete(capture.previewUrl)
    const next = new Map(capturesBySlide)
    next.delete(slideId)
    setCapturesBySlide(next)
  }

  async function startPresentation() {
    flushSync(() => {
      setPresenting(true)
      setSlideIndex(0)
    })
    const fullscreenRequest = presentationRef.current?.requestFullscreen?.()
    void fullscreenRequest?.catch(() => undefined)
  }

  async function stopPresentation() {
    setPresenting(false)
    if (document.fullscreenElement) await document.exitFullscreen().catch(() => undefined)
  }

  async function createEdition() {
    setGenerating(true)
    setMessage(null)
    try {
      const id = crypto.randomUUID()
      const editionId = editionIdFor(project, scope)
      const createdAt = new Date().toISOString()
      const visibleSlideIds = new Set(slides.map((slide) => slide.id))
      const captures = Array.from(capturesBySlide.values()).filter((capture) => visibleSlideIds.has(capture.binding.slide_id))
      for (const capture of captures) {
        if (!captureMatchesPresentation(capture.binding, {
          draftId,
          slideId: capture.binding.slide_id,
          scope,
          projection,
          referenceProjection,
        })) throw new Error('Une vue 3D ne correspond plus à cette version de la présentation.')
      }
      const source = buildPresentationSourceSnapshot(project, attachments, config, editionId, createdAt, captures.map((capture) => capture.binding))
      const captureBlobs = new Map(captures.map((capture) => [capture.binding.id, capture.blob]))
      const pptx = await buildPresentationPowerPoint(source, undefined, captureBlobs)
      const pptxSha256 = await sha256Blob(pptx)
      const edition = await onPublish({ id, editionId, config, source, pptx, pptxSha256 })
      setLatestEditionId(edition.id)
      setMessage({ tone: 'success', text: `L’édition ${edition.edition_id} est figée. Le PowerPoint est prêt.` })
    } catch (error) {
      setMessage({ tone: 'error', text: error instanceof Error ? error.message : 'L’édition n’a pas pu être créée.' })
    } finally {
      setGenerating(false)
    }
  }

  return (
    <main className="presentation-studio workspace wide-workspace">
      <div className="presentation-studio-title">
        <div>
          <button className="back-link" onClick={onBack} type="button"><ArrowLeft size={17} /> {scope === 'general' ? 'Retour à la référence' : 'Retour au cockpit'}</button>
          <span className="eyebrow">STUDIO DE PRÉSENTATION</span>
          <h1>Préparer une présentation</h1>
          <p>{scope === 'general' ? 'Préparez le parcours administratif général.' : `Préparez le point d’avancement de ${project.name}.`}</p>
        </div>
        <button className="primary-button" disabled={generating} onClick={() => void createEdition()} type="button">
          {generating ? <LoaderCircle className="spin" size={18} /> : <FileText size={18} />}
          {generating ? 'Création du PowerPoint…' : 'Créer l’édition PowerPoint'}
        </button>
      </div>

      {message ? <div className={`presentation-message ${message.tone}`} role="status">{message.tone === 'success' ? <Check size={18} /> : <X size={18} />}{message.text}</div> : null}

      <section className="presentation-studio-grid">
        <aside className="presentation-controls">
          <header><Presentation size={19} /><div><strong>Préparation</strong><span>{scope === 'general' ? 'Référence générale' : project.name}</span></div></header>
          <label className="presentation-field"><span>Audience <HelpTooltip text="Indiquez les personnes auxquelles la présentation est destinée." label="À quoi sert l’audience ?" align="left" /></span><input maxLength={255} onChange={(event) => setConfig((current) => ({ ...current, audience: event.target.value }))} value={config.audience} /></label>
          <label className="presentation-field"><span>Objectif de la rencontre</span><textarea maxLength={1000} onChange={(event) => setConfig((current) => ({ ...current, objective: event.target.value }))} rows={3} value={config.objective} /></label>
          <label className="presentation-field"><span>Format de présentation</span><select aria-label="Format de présentation" onChange={(event) => { setConfig((current) => ({ ...current, duration: event.target.value as PresentationDuration })); setSlideIndex(0) }} value={config.duration}>{Object.entries(PRESENTATION_DEPTH_LABELS).map(([value, option]) => <option key={value} value={value}>{option.label}</option>)}</select><small>{PRESENTATION_DEPTH_LABELS[config.duration].description}</small></label>
          {scope === 'project' && attachments.length ? <fieldset className="presentation-document-selection"><legend>Documents à présenter</legend>{attachments.map((attachment) => <label className="presentation-attachment-choice" key={attachment.id}><input checked={config.selectedAttachmentIds.includes(attachment.id)} onChange={() => toggleAttachment(attachment.id)} type="checkbox" /><span>{attachment.title || attachment.filename}<small>{attachment.category || `Version ${attachment.version_number}`}</small></span></label>)}</fieldset> : null}
        </aside>

        <section className="presentation-preview-panel">
          <header><div><Presentation size={19} /><span><strong>Aperçu · {scope === 'general' ? `référence v${project.version}` : `projet v${project.version}`}</strong><small>{slides.length} diapositives · {scope === 'general' ? `référence du ${formatDate(project.snapshot.reference.effective_date)}` : `situation au ${formatDate(project.snapshot.as_of_date)}`}</small></span></div><button className="secondary-button compact" onClick={() => void startPresentation()} type="button"><Expand size={16} /> Présenter en plein écran</button></header>
          <PresentationSlideCanvas capture={currentCapture} slide={currentSlide} />
          {currentSlide && currentSlide.key !== 'cover' && currentSlide.key !== 'edition' ? (
            <div className="presentation-capture-controls">
              {currentCapture ? (
                <>
                  <span className="presentation-capture-status"><Check size={16} /> Vue 3D ajoutée</span>
                  <button onClick={() => setCaptureSlideId(currentSlide.id)} type="button"><RefreshCw size={16} /> Remplacer la vue 3D</button>
                  <button onClick={() => removeCapture(currentSlide.id)} type="button"><Trash2 size={16} /> Supprimer la vue 3D</button>
                </>
              ) : (
                <button className="secondary-button presentation-capture-open" onClick={() => setCaptureSlideId(currentSlide.id)} type="button"><Camera size={17} /> Capturer une vue 3D</button>
              )}
            </div>
          ) : null}
          <footer className="presentation-preview-nav"><button aria-label="Diapositive précédente" disabled={slideIndex === 0} onClick={() => setSlideIndex((current) => Math.max(0, current - 1))} type="button"><ChevronLeft /></button><div>{slides.map((slide, index) => <button aria-label={`Afficher la diapositive ${index + 1}`} className={index === slideIndex ? 'active' : ''} key={`${slide.key}-${index}`} onClick={() => setSlideIndex(index)} type="button" />)}</div><span>{slideIndex + 1} / {slides.length}</span><button aria-label="Diapositive suivante" disabled={slideIndex === slides.length - 1} onClick={() => setSlideIndex((current) => Math.min(slides.length - 1, current + 1))} type="button"><ChevronRight /></button></footer>
          <div className="presentation-output-explanation"><MonitorPlay size={19} /><p><strong>Projection</strong><span>Ouvrir l’aperçu en plein écran.</span></p><FileText size={19} /><p><strong>PowerPoint</strong><span>Télécharger une version éditable.</span></p></div>
        </section>
      </section>

      <section className="presentation-editions">
        <header><div><h2>Éditions figées</h2><p>Une nouvelle édition n’écrase jamais celles qui existent déjà.</p></div><HelpTooltip text="Une édition figée conserve sa source, son format et les empreintes exactes de ses deux fichiers." label="Qu’est-ce qu’une édition figée ?" align="right" /></header>
        {editions.length ? <div className="presentation-edition-list">{editions.map((edition) => <article className={latestEditionId === edition.id ? 'latest' : ''} key={edition.id}><div><span className="presentation-edition-icon"><Presentation size={19} /></span><span><strong>{edition.title}</strong><small>{edition.edition_id} · {edition.scope === 'general' ? `référence v${edition.source_reference_version}` : `projet v${edition.source_project_version}`} · {formatDate(edition.as_of_date)}</small></span></div><span className="presentation-edition-scope">{edition.scope === 'general' ? 'Parcours général' : 'Point projet'}</span><time dateTime={edition.created_at}>{new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(edition.created_at))}</time><div className="presentation-edition-downloads"><button onClick={() => void onDownload(edition)} type="button"><Download size={15} /> PowerPoint</button></div></article>)}</div> : <div className="presentation-empty-editions"><Presentation size={28} /><strong>Aucune édition figée pour le moment</strong><span>L’aperçu reste librement modifiable. Créez une édition quand le récit est prêt.</span></div>}
      </section>

      {presenting ? <div className="presentation-fullscreen" ref={presentationRef} tabIndex={-1}><button className="presentation-fullscreen-close" onClick={() => void stopPresentation()} type="button"><X size={20} /> Quitter</button><PresentationSlideCanvas capture={currentCapture} large slide={currentSlide} /><button className="presentation-fullscreen-arrow left" aria-label="Précédent" disabled={slideIndex === 0} onClick={() => setSlideIndex((current) => Math.max(0, current - 1))} type="button"><ChevronLeft /></button><button className="presentation-fullscreen-arrow right" aria-label="Suivant" disabled={slideIndex === slides.length - 1} onClick={() => setSlideIndex((current) => Math.min(slides.length - 1, current + 1))} type="button"><ChevronRight /></button><div className="presentation-fullscreen-counter">{slideIndex + 1} / {slides.length}</div></div> : null}

      {captureSlide ? <Suspense fallback={<div className="capture-workbench-backdrop"><div className="global-loading capture-workbench-loading"><LoaderCircle className="spin" /> Préparation de la vue 3D…</div></div>}><CaptureWorkbench draftId={draftId} initialCapture={capturesBySlide.get(captureSlide.id)?.binding} onCancel={() => setCaptureSlideId(null)} onCapture={keepCapture} projection={projection} referenceProjection={referenceProjection} replaceExisting={capturesBySlide.has(captureSlide.id)} scope={scope} slideId={captureSlide.id} slideTitle={captureSlide.title} /></Suspense> : null}
    </main>
  )
}
